// Reusable info icon + status-chip tip catalog.
//
// Two exports:
//   InfoIcon     — generic, takes `text` prop, hover/click tooltip.
//   InfoIconFor  — takes `label` prop, looks up STATUS_TIPS for the copy.
//
// Both render the same dark-themed MUI Tooltip + InfoOutlinedIcon. One
// implementation; every page imports from here.

import React from 'react';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { THEME } from '../../pages/enterprise-ai/genesisData';

export const STATUS_TIPS = {
  shipped: 'The deck was generated and sent to the customer. Does not by itself mean the deal closed.',
  signed: 'The customer signed the LOI. The deal is closed.',
  live: 'Built and running in production. Only LIVE capabilities can be promised in present tense in the customer deck.',
  planned: 'On the roadmap, not built yet. Cannot be claimed as live; moves to Build Commitments instead.',
  pending: 'Awaiting a decision or input. No action taken yet.',
  'in progress': 'Currently running. Some stages are complete; later stages have not started.',
  'in-progress': 'Currently running. Some stages are complete; later stages have not started.',
  queued: 'Not yet started. Waiting on the upstream pipeline layer to finish first.',
  running: 'Currently executing. Updates stream in as the stage completes.',
  extracted: 'Pulled directly from the source transcript. Not synthesized or guessed.',
  computed: 'Calculated from upstream values (transcript facts, capability registry, coordination cost).',
  registry: 'Looked up live from the platform capability registry. Reflects what we can actually do today.',
  human: 'Added by the salesperson, not the AI. Visual assets and build commitments are human-curated.',
  max: 'This was the highest of the three pricing anchors, so it sets the floor for Phase 3 monthly pricing.',
  'user-added': 'A salesperson added this manually; it was not extracted from the call.',
  won: 'Final outcome: the customer signed. This run is canonical for matching future similar customers.',
  lost: 'Deal did not close. Run remains in Memory as a counterexample for future similar runs.',
  no_decision: 'Deal stalled with no outcome recorded. Treated as low-signal in Memory.',
  pass: 'Scored above the deploy threshold on this suite. Safe to ship.',
  borderline: 'Passed overall but one or more individual checks fell below threshold. Worth investigating.',
  fail: 'Scored below the threshold. Blocks deploy until fixed.',
  high: 'Most workflows, vendors, and numbers were captured directly from the call. Trust the extraction.',
  medium: 'Some fields are inferred from context. Spot-check before committing.',
  low: 'Heavy inference. Most fields were not directly stated; treat as a starting draft.',
  active: 'Currently running on the customer calendar.',
  paused: 'Stopped on purpose. Will resume when the customer is ready.',
  planning: 'Scheduled but has not started its first run.',
  completed: 'Job ran end to end and finished cleanly.',
  failed: 'Job started but did not finish. Logs show the failure point.',
};

const tipSx = {
  background: THEME.cardRaised,
  color: THEME.textPrimary,
  fontSize: 11,
  lineHeight: 1.5,
  padding: '8px 10px',
  maxWidth: 260,
  border: `1px solid ${THEME.border}`,
};

const iconSx = {
  fontSize: 12,
  color: THEME.textFaint,
  ml: 0.5,
  cursor: 'help',
  verticalAlign: 'middle',
  '&:hover': { color: THEME.textSecondary },
};

export const InfoIcon = ({ text }) => {
  if (!text) return null;
  return (
    <Tooltip
      title={text}
      arrow
      placement="top"
      componentsProps={{
        tooltip: { sx: tipSx },
        arrow: { sx: { color: THEME.cardRaised } },
      }}
    >
      <InfoOutlinedIcon sx={iconSx} />
    </Tooltip>
  );
};

export const InfoIconFor = ({ label }) => {
  if (!label) return null;
  const key = String(label).toLowerCase();
  const text = STATUS_TIPS[key];
  return text ? <InfoIcon text={text} /> : null;
};

export default InfoIcon;
